import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';

// Material UI icons
import {
  ArrowBack as ArrowBackIcon,
  Warehouse as WarehouseIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  SwapHoriz as MoveIcon
} from '@mui/icons-material';

// Services
import { getLocationInventory } from '../../services/locationService';

// Components
import MovePartDialog from '../../components/MovePartDialog';

const LocationInventory = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [location, setLocation] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch location inventory data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getLocationInventory(id);
        setLocation(data.location);
        setInventory(data.inventory);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching location inventory:', err);
        setError('Failed to load location inventory. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle move dialog
  const handleMoveClick = (item) => {
    setSelectedItem(item);
    setMoveDialogOpen(true);
  };

  const handleMoveDialogClose = () => {
    setMoveDialogOpen(false);
    setSelectedItem(null);
  };

  const handleMoveSuccess = () => {
    // Refresh the inventory data after successful move
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getLocationInventory(id);
        setLocation(data.location);
        setInventory(data.inventory);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching location inventory:', err);
        setError('Failed to load location inventory. Please try again.');
        setLoading(false);
      }
    };
    fetchData();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/locations')}
        >
          Back to Locations
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Dashboard
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/locations');
          }}
        >
          Locations
        </Link>
        <Typography color="text.primary">
          Location Inventory
        </Typography>
      </Breadcrumbs>

      {/* Location details card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" component="h1" gutterBottom>
                Location: Rack {location.rack} - Shelf {location.shelf}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarehouseIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  {location.warehouse.name} ({location.warehouse.location})
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Barcode: {location.barcode}
              </Typography>
            </Box>

            <Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/locations/edit/${location.id}`)}
                sx={{ mr: 1 }}
              >
                Edit Location
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/inventory/adjust?location_id=${location.id}`)}
              >
                Add Inventory
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Inventory table */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Inventory at this Location
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {inventory.length} {inventory.length === 1 ? 'item' : 'items'}
          </Typography>
        </Box>

        <Divider />

        {inventory.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No inventory found at this location
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/inventory/adjust?location_id=${location.id}`)}
              sx={{ mt: 2 }}
            >
              Add Inventory
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Part Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.part.internal_part_number}
                      {item.part.is_special_order && (
                        <Chip
                          label="Special Order"
                          size="small"
                          color="secondary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{item.part.description}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="center">
                      {item.below_reorder_point ? (
                        <Chip
                          icon={<WarningIcon />}
                          label="Low Stock"
                          color="error"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="In Stock"
                          color="success"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Move to Another Location">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleMoveClick(item)}
                          >
                            <MoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Adjust Quantity">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/inventory/adjust?part_id=${item.part.id}&location_id=${location.id}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Part Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/inventory/${item.part.id}`)}
                          >
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Back button */}
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/locations')}
      >
        Back to Locations
      </Button>

      {/* Move Part Dialog */}
      {selectedItem && (
        <MovePartDialog
          open={moveDialogOpen}
          onClose={handleMoveDialogClose}
          part={selectedItem.part}
          currentLocation={location}
          currentQuantity={selectedItem.quantity}
          onSuccess={handleMoveSuccess}
        />
      )}
    </Container>
  );
};

export default LocationInventory;